import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { goodsIntakeApi } from '../../api/goods-intake-api';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import { SearchableSelect } from '../../components/SearchableSelect';
import { validateReceiptInput, type ExpectedReceipt } from '../../domain/goods-intake';
import type { StockReferences } from '../stock/use-stock-references';
import { intakeErrorMessage, isUnknownOutcome } from './error-message';
import { INTAKE_QUERY_KEYS } from './use-intake-queries';

type Row = { key: string; stockItemId: string; quantity: string; locationId: string };

const newRow = (): Row => ({
  key: crypto.randomUUID(),
  stockItemId: '',
  quantity: '',
  locationId: '',
});

/**
 * Announces goods ("expected") or records goods that arrived without a
 * manifest ("arrived"). An arrival is created as a receipt and counted in
 * the same step, then reviewed and posted like any other receipt.
 */
export function NewReceiptDrawer({
  mode,
  references,
  onClose,
  onCreated,
}: {
  mode: 'expected' | 'arrived';
  references: StockReferences;
  onClose: () => void;
  onCreated: (receipt: ExpectedReceipt, notice?: string) => void;
}) {
  const queryClient = useQueryClient();
  const receivingSites = useMemo(
    () => [...new Set(references.locations.filter((l) => l.active).map((l) => l.siteId))],
    [references.locations],
  );
  const [siteId, setSiteId] = useState(
    receivingSites.length === 1 ? (receivingSites[0] ?? '') : '',
  );
  const [supplier, setSupplier] = useState('');
  const [supplierReference, setSupplierReference] = useState('');
  const [identifiers, setIdentifiers] = useState('');
  const [expectedAt, setExpectedAt] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);
  const arrived = mode === 'arrived';
  const title = arrived ? 'Receive without a manifest' : 'New expected receipt';

  const itemOptions = useMemo(
    () =>
      references.items
        .filter((item) => item.active)
        .map((item) => ({
          value: item.id,
          label: references.itemName(item.id),
          aliases: [item.sku, ...item.barcodes],
        })),
    [references],
  );
  const locationOptions = useMemo(
    () =>
      references.locations
        .filter((l) => l.active && l.siteId === siteId)
        .map((l) => ({ value: l.id, label: references.locationName(l.id), aliases: [l.code] })),
    [references, siteId],
  );

  const create = useMutation({
    mutationFn: async (): Promise<{ receipt: ExpectedReceipt; notice?: string }> => {
      const receipt = await goodsIntakeApi.createReceipt({
        siteId,
        supplier,
        supplierReference,
        identifiers: identifiers
          .split(/[,;\n]+/)
          .map((id) => id.trim())
          .filter(Boolean),
        expectedAt: expectedAt ? new Date(`${expectedAt}T09:00:00`).toISOString() : undefined,
        notes,
        lines: rows.map((row) => ({
          stockItemId: row.stockItemId,
          expectedQuantity: Number.parseInt(row.quantity, 10),
        })),
      });
      if (!arrived) return { receipt };
      try {
        const counted = await goodsIntakeApi.recordIntake(
          receipt.id,
          receipt.version,
          rows.map((row) => ({
            stockItemId: row.stockItemId,
            receivedQuantity: Number.parseInt(row.quantity, 10),
            damagedQuantity: 0,
            locationId: row.locationId || undefined,
          })),
        );
        return { receipt: counted };
      } catch {
        // The receipt exists; the counts can be entered on its page.
        return {
          receipt,
          notice: 'The receipt was created, but the counts weren’t saved. Enter them below.',
        };
      }
    },
    onSuccess: async ({ receipt, notice }) => {
      await queryClient.invalidateQueries({ queryKey: INTAKE_QUERY_KEYS.receipts });
      onCreated(receipt, notice);
    },
    onError: (error: unknown) => {
      if (isUnknownOutcome(error)) setUnknownOutcome(true);
      else setFormError(intakeErrorMessage(error, 'The receipt could not be created.'));
    },
  });

  function update(key: string, patch: Partial<Row>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    const problem = validateReceiptInput({
      siteId,
      identifiers: [],
      lines: rows.map((row) => ({
        stockItemId: row.stockItemId,
        expectedQuantity: Number(row.quantity),
      })),
    });
    if (problem) {
      setFormError(problem);
      return;
    }
    if (arrived && rows.some((row) => !row.locationId)) {
      setFormError('Choose where each line goes.');
      return;
    }
    create.mutate();
  }

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer drawer-wide"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={`Close ${title} drawer`}
          >
            ×
          </button>
        </div>
        <p className="field-hint">
          {arrived
            ? 'For goods from a supplier without an advance notice. Enter what arrived; you review it before it goes into stock.'
            : 'Goods on their way. Scanning the parcel or pallet number on arrival opens this receipt.'}
        </p>
        {unknownOutcome ? (
          <PartialFailureNotice
            onCheckCurrentState={() => {
              void queryClient.invalidateQueries({ queryKey: INTAKE_QUERY_KEYS.receipts });
              onClose();
            }}
          />
        ) : null}

        <label>
          Receiving site
          <SearchableSelect
            ariaLabel="Receiving site"
            value={siteId}
            onChange={(value) => {
              setSiteId(value);
              setRows((current) => current.map((row) => ({ ...row, locationId: '' })));
            }}
            options={receivingSites.map((id) => ({ value: id, label: references.siteName(id) }))}
            placeholder="Choose a site with stock locations"
          />
        </label>
        <div className="form-row">
          <label>
            Supplier
            <input value={supplier} onChange={(event) => setSupplier(event.target.value)} />
          </label>
          <label>
            Order or delivery note number
            <input
              value={supplierReference}
              onChange={(event) => setSupplierReference(event.target.value)}
            />
          </label>
        </div>
        {arrived ? null : (
          <div className="form-row">
            <label>
              Parcel, pallet or shipment numbers
              <input
                value={identifiers}
                onChange={(event) => setIdentifiers(event.target.value)}
                placeholder="Separate several with commas"
              />
            </label>
            <label>
              Expected on
              <input
                type="date"
                value={expectedAt}
                onChange={(event) => setExpectedAt(event.target.value)}
              />
            </label>
          </div>
        )}

        <fieldset className="plain-fieldset intake-new-lines">
          <legend>{arrived ? 'What arrived' : 'What is expected'}</legend>
          {rows.map((row, index) => (
            <div key={row.key} className="intake-new-line">
              <SearchableSelect
                ariaLabel={`Line ${index + 1} stock item`}
                value={row.stockItemId}
                onChange={(value) => update(row.key, { stockItemId: value })}
                options={itemOptions}
                placeholder="Stock item"
              />
              <input
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                aria-label={`Line ${index + 1} quantity`}
                value={row.quantity}
                onChange={(event) => update(row.key, { quantity: event.target.value })}
                placeholder="Qty"
              />
              {arrived ? (
                <SearchableSelect
                  ariaLabel={`Line ${index + 1} location`}
                  value={row.locationId}
                  onChange={(value) => update(row.key, { locationId: value })}
                  options={locationOptions}
                  placeholder={siteId ? 'Put away at' : 'Choose a site first'}
                  disabled={!siteId}
                />
              ) : null}
              <button
                type="button"
                className="link-button"
                onClick={() => setRows((current) => current.filter((r) => r.key !== row.key))}
                disabled={rows.length === 1}
                aria-label={`Remove line ${index + 1}`}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" onClick={() => setRows((current) => [...current, newRow()])}>
            Add line
          </button>
        </fieldset>
        <label>
          Notes
          <textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={2} />
        </label>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}
        <div className="drawer-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={create.isPending || unknownOutcome}>
            {create.isPending ? 'Saving…' : arrived ? 'Review what arrived' : 'Create receipt'}
          </button>
        </div>
      </form>
    </div>
  );
}
